import _ from "lodash";
import { format } from "date-fns";


enum NativeAppPlatform {
  SALESFORCE_NATIVE = "SALESFORCE_NATIVE",
  MS_DYNAMICS_NATIVE = "MS_DYNAMICS_NATIVE",
}

type SortOrder = "asc" | "desc";

export const getPlatformFromDomain = (domain: string): NativeAppPlatform | null => {
  if (!_.isString(domain) || _.isEmpty(domain)) return null;

  const cleanDomain = _.toLower(_.trim(domain));

  if (_.endsWith(cleanDomain, "lightning.force.com")) {
    return NativeAppPlatform.SALESFORCE_NATIVE;
  } else if (_.endsWith(cleanDomain, "crm.dynamics.com")) {
    return NativeAppPlatform.MS_DYNAMICS_NATIVE;
  }

  return null;
};

/**
 * Formats a date string into "dd MMM yyyy" format using the browser's locale.
 * Example: "2025-09-03" → "03 Sep 2025"
 *
 * @param dateString - A valid date string (e.g., "2025-09-03T12:00:00Z")
 * @returns Formatted date string in "dd MMM yyyy" format
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Formats a date (ISO string or timestamp) into "do MMM yyyy" format using date-fns.
 * Adds ordinal suffix to the day (e.g., "1st", "2nd", "3rd").
 * Example: "2025-09-03" → "3rd Sep 2025"
 *
 * @param iso - ISO date string or timestamp (number)
 * @returns Formatted date string with ordinal day
 */
export const formatDateWithOrdinal = (iso: string | number): string => {
  return format(new Date(iso), "do MMM yyyy");
};

/**
 * Filter search and sort an array of objects.
 *
 * @param {Array<Object>} data - The array of items to filter and sort.
 * @param {string | null} searchQuery - Text to search for in the specified fields.
 * @param {Array<string> | null} searchFields - Object keys to check for the search text.
 * @param {string | null} sortBy - Key to sort the results by.
 * @param {string | null} sortOrder - Sort direction: "asc" for ascending, "desc" for descending.
 * @param {boolean | null} prefixSearch - If provided, enables exact prefix matching only.
 *
 * @returns {Array<Object>} - Filtered and sorted array.
 * 
 * Example usage:
 * filterSearchAndSort(sampleDate, 'e', ['name', 'content'], 'createdDate', 'desc', true)
 */
export const applySearchAndSort = (
  data: any[],
  searchQuery: string,
  searchFields: string[],
  sortBy?: string,
  sortOrder?: string,
  prefixSearch?: boolean
 ): any[] => {
    if (!data || _.isEmpty(data)) return [];
   
    let result = data;
      
    // Filter by searchQuery in given searchFields
    if(searchQuery && !_.isEmpty(searchFields)){
      const lowerCaseQuery = _.toLower(searchQuery); // searchquery
      result = _.filter(data, (item) => {
              for(let key of searchFields){ // fields to be searched
                  const value: any = _.get(item, key, ""); // get value of key from item
                  if(value){
                      const lowerCaseValue = _.toLower(value);

                      if (prefixSearch) { // prefix matchs
                        if (_.startsWith(lowerCaseValue, lowerCaseQuery)) {
                          return true;
                        }
                      } 
                      
                      if (!prefixSearch) {// normal contains
                        if (_.includes(lowerCaseValue, lowerCaseQuery)) {
                          return true;
                        }
                      }
                     
                  }
              }
              return false;
      });
    }

    // Sort by
    if(sortBy && sortOrder){
      result = _.orderBy(result, [sortBy], [sortOrder.toLowerCase() as SortOrder]);
    } 

    return result;  
}

//custom dropdown

interface IDropdownPosition {
  top: string | number;
  bottom: string | number;
  left: string;
}

interface ICalculateDropdownPositionParams {
  editorRef: any;
  dropdownHeight?: number;
  dropdownWidth?: number;
  padding?: number;
  maxHeight?: string;
}

export const calculateDropdownPosition = ({
  editorRef,
  dropdownHeight = 200,
  dropdownWidth = 200,
  padding = 10
}: ICalculateDropdownPositionParams): IDropdownPosition | null => {
  if (!editorRef?.current) return null;

  const selection = editorRef.current.selection;
  const rng = selection.getRng(); // Get the current position of text in the editor
  const rect = rng.getBoundingClientRect(); // Get the exact position of the cursor

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  let top: string | number = 'auto';
  let bottom: string | number = 'auto';

  if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
    bottom = window.innerHeight - rect.top + 5;
  } else {
    top = rect.bottom + 5;
  }

  let left = rect.left;
  if (left + dropdownWidth > window.innerWidth) {
    left = window.innerWidth - dropdownWidth - padding;
  }

  return {
    top,
    bottom,
    left: `${left}px`
  };
};

export const getDropdownStyle = (
  position: IDropdownPosition | null
): React.CSSProperties => { 
  if (!position) return {};

  return {
    position: 'fixed',
    top: typeof position.top === 'number' ? `${position.top}px` : position.top,
    bottom: typeof position.bottom === 'number' ? `${position.bottom}px` : position.bottom,
    left: position.left,
    backgroundColor: 'white',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '5px',
    maxHeight: 'auto',
    overflowY: 'auto',
    zIndex: 10000,
    fontSize: '11px',
    fontFamily: 'Gilroy',
    minWidth: '150px',
    boxShadow: '0px 0px 20px 0px rgba(18, 18, 18, 0.05)',
  };
};